import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import {
  Plus, AlertTriangle, CheckCircle2, Clock, XCircle, MapPin, MessageSquare,
} from "lucide-react";

const SEVERITY_OPTIONS = [
  { value: "low",     label: "Low",     tone: "default" },
  { value: "normal",  label: "Normal",  tone: "blue" },
  { value: "high",    label: "High",    tone: "amber" },
  { value: "urgent",  label: "Urgent",  tone: "red" },
];

const STATUS_META = {
  open:       { label: "Open",       Icon: AlertTriangle, color: "#EAB308" },
  in_review:  { label: "In review",  Icon: Clock,         color: "#0A84FF" },
  resolved:   { label: "Resolved",   Icon: CheckCircle2,  color: "#22C55E" },
  dismissed:  { label: "Dismissed",  Icon: XCircle,       color: "#6B7280" },
};

const empty = { subject: "", body: "", severity: "normal", site_id: "" };


/**
 * Client-portal Complaints page.
 * Route: /portal/complaints
 *
 * Clients (and their site/regional managers) can:
 *   • File a new complaint with subject / severity / detail / optional site.
 *   • See their complaint history + status transitions applied by admin.
 */
export default function PortalComplaints() {
  const [rows, setRows]     = useState([]);
  const [sites, setSites]   = useState([]);
  const [open, setOpen]     = useState(false);
  const [form, setForm]     = useState(empty);
  const [busy, setBusy]     = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/portal/complaints"),
        api.get("/portal/sites").catch(() => ({ data: [] })),
      ]);
      setRows(Array.isArray(r.data) ? r.data : []);
      setSites(Array.isArray(s.data) ? s.data : (s.data?.items || []));
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load complaints"));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Subject and detail are both required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/portal/complaints", {
        subject:  form.subject.trim(),
        body:     form.body.trim(),
        severity: form.severity,
        site_id:  form.site_id || null,
      });
      toast.success("Complaint logged. Your account manager has been notified.");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to file complaint"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="portal-complaints-page">
      <PageHeader
        title="Complaints"
        subtitle="Log a service concern or escalation. Your account manager is notified in real-time and will follow up. Prior complaints and their resolution status are listed below."
        action={
          <ActionButton
            data-testid="new-complaint-btn"
            onClick={() => { setForm(empty); setOpen(true); }}
          >
            <Plus className="size-4" /> New complaint
          </ActionButton>
        }
      />

      <div className="p-8">
        {loading ? (
          <div className="border border-[#27272A] bg-[#0A0A0A] p-16 text-center text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-[#27272A] bg-[#0A0A0A] p-12 text-center">
            <div className="label-mute mb-2">No complaints on file</div>
            <div className="text-zinc-400 text-sm">
              You haven&apos;t raised any complaints yet. If something needs attention, log it here — your account manager sees it immediately.
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="complaints-list">
            {rows.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.open;
              const Icon = meta.Icon;
              return (
                <div
                  key={c.id}
                  className="border border-[#27272A] bg-[#121212] p-5 hover:border-[#3F3F46] transition-colors"
                  data-testid={`complaint-row-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border"
                          style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}12` }}
                        >
                          <Icon className="size-3" /> {meta.label}
                        </span>
                        <PillBadge tone={SEVERITY_OPTIONS.find((s) => s.value === c.severity)?.tone || "default"}>
                          {c.severity}
                        </PillBadge>
                        <span className="text-xs text-zinc-500 font-mono">
                          {new Date(c.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                        {c.site_id && sites.find((s) => s.id === c.site_id) && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                            <MapPin className="size-3" /> {sites.find((s) => s.id === c.site_id).name}
                          </span>
                        )}
                      </div>
                      <div className="font-display font-bold text-lg tracking-tight mt-2">{c.subject}</div>
                      <div className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap">{c.body}</div>
                      {c.resolution_notes && (
                        <div className="mt-3 border-l-2 border-[#22C55E] pl-3 text-sm text-zinc-300">
                          <div className="label-mute mb-1 flex items-center gap-1.5">
                            <MessageSquare className="size-3" />
                            Response from your account manager
                          </div>
                          <div className="whitespace-pre-wrap">{c.resolution_notes}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)} title="File a complaint" testId="new-complaint-modal">
          <div className="space-y-4">
            <FormField label="Subject *">
              <input
                data-testid="complaint-subject"
                className={inputClass}
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="Short summary — e.g. Officer was late for morning shift"
                maxLength={180}
              />
            </FormField>
            <FormField label="Severity">
              <select
                data-testid="complaint-severity"
                className={inputClass}
                value={form.severity}
                onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
              >
                {SEVERITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </FormField>
            {sites.length > 0 && (
              <FormField label="Site (optional)">
                <select
                  data-testid="complaint-site"
                  className={inputClass}
                  value={form.site_id}
                  onChange={(e) => setForm((f) => ({ ...f, site_id: e.target.value }))}
                >
                  <option value="">— Not site-specific —</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </FormField>
            )}
            <FormField label="Detail *">
              <textarea
                data-testid="complaint-body"
                rows={6}
                className={inputClass}
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="What happened, when, and what would resolve it for you?"
              />
            </FormField>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
              <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
              <ActionButton
                data-testid="complaint-submit"
                onClick={submit}
                disabled={busy}
                className={busy ? "opacity-50 cursor-not-allowed" : ""}
              >
                {busy ? "Filing…" : "File complaint"}
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
